'use client';
import ReactMarkdown, { Options } from 'react-markdown';
import { ReactNode } from 'react';
import strip from 'remark-strip-html';
import remarkGfm from 'remark-gfm';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image';
import { appConfig } from '../../utils/appConfig';
import TikTokEmbed from './TikTokEmbed';

interface Props extends Options {
  bodySize?: 'xLarge' | 'large' | 'medium' | 'small';
  children?: string;
  className?: string;
  components?: any;
  remarkPlugins?: any[];
}

export const Markdown = ({
  children,
  components = {},
  className,
  bodySize = 'medium',
  remarkPlugins = [],
  ...props
}: Props) => {
  const bodyClassName = classNames(
    bodySize === 'xLarge' && 'tra-text-web-body-xl',
    bodySize === 'large' && 'tra-text-web-body-l',
    bodySize === 'medium' && 'tra-text-web-body-m',
    bodySize === 'small' && 'tra-text-web-body-s',
    className,
  );

  return (
    <ReactMarkdown
      remarkPlugins={[strip, remarkGfm, ...(remarkPlugins || [])]}
      components={{
        a: (elProps: any) => {
          const childText = Array.isArray(elProps.children)
            ? elProps.children[0]
            : typeof elProps.children === 'string'
              ? elProps.children
              : '';
          if (childText === 'tiktok embed') {
            return <TikTokEmbed href={elProps.href} />;
          }
          const isInternal = elProps.href?.startsWith('/') || elProps.href?.startsWith('#');
          return (
            <Anchor
              className={classNames('tra-text-web-link', elProps.className)}
              externalLink={!isInternal ? elProps.href : undefined}
              link={isInternal ? elProps.href : undefined}
              isExternal={!isInternal}
              {...elProps}
            />
          );
        },
        p: ({ children }: { children: ReactNode }) => (
          <p className={classNames('tw-mb-4', bodyClassName)}>{children}</p>
        ),
        h1: ({ children }: { children: ReactNode }) => (
          <h1 className="tra-text-web-heading-1 tw-mb-4">{children}</h1>
        ),
        h2: ({ children }: { children: ReactNode }) => (
          <h2 className="tra-text-web-heading-2 tw-mb-4">{children}</h2>
        ),
        h3: ({ children }: { children: ReactNode }) => (
          <h3 className="tra-text-web-heading-3 tw-mb-4">{children}</h3>
        ),
        h4: ({ children }: { children: ReactNode }) => (
          <h4 className="tra-text-web-heading-4 tw-mb-4">{children}</h4>
        ),
        h5: ({ children }: { children: ReactNode }) => (
          <h5 className="tra-text-web-heading-5 tw-mb-4">{children}</h5>
        ),
        h6: ({ children }: { children: ReactNode }) => (
          <h6 className="tra-text-web-heading-6 tw-mb-4">{children}</h6>
        ),
        ul: ({ children }: { children: ReactNode }) => (
          <ul className="tw-pl-5 tw-my-4">{children}</ul>
        ),
        ol: ({ children, start }: { children: ReactNode; start?: number }) => (
          <ol className="tw-pl-5" start={start}>
            {children}
          </ol>
        ),
        li: ({ children }: { children: ReactNode }) => (
          <li className={classNames('tw-pb-2', bodyClassName)}>{children}</li>
        ),
        table: ({ children }: { children: ReactNode }) => (
          <div className="tw-overflow-x-auto md:tw-my-6 tw-border tw-border-warm-100 tw-bg-white">
            <table className="tw-min-w-full tw-border-collapse">{children}</table>
          </div>
        ),
        thead: ({ children }: { children: ReactNode }) => (
          <thead className={classNames('tw-bg-warm-100', appConfig.isMeater && 'tw-bg-white')}>
            {children}
          </thead>
        ),
        tbody: ({ children }: { children: ReactNode }) => (
          <tbody
            className={classNames(
              'tw-bg-white tw-divide-y tw-divide-gray-100',
              appConfig.isMeater && '[&>tr:nth-child(odd)]:tw-bg-[#eef2f6]',
            )}
          >
            {children}
          </tbody>
        ),
        tr: ({ children }: { children: ReactNode }) => (
          <tr className="tw-group tw-hover:bg-gray-50 tw-transition-all tw-duration-150">
            {children}
          </tr>
        ),
        th: ({ children }: { children: ReactNode }) => (
          <th
            className={classNames(
              'tw-px-4 tw-py-3 tw-text-left tra-text-web-body-l tw-font-bold first:tw-pl-6 last:tw-pr-6 md:tw-px-6 md:tw-py-4',
              appConfig.isMeater && 'tw-uppercase',
            )}
          >
            {children}
          </th>
        ),
        td: ({ children }: { children: ReactNode }) => (
          <td className="tw-px-4 tw-py-3 tra-text-web-body-l tw-border-b tw-border-warm-100 first:tw-pl-6 last:tw-pr-6 md:tw-px-6 md:tw-py-4">
            {children}
          </td>
        ),
        hr: ({ children }: { children: ReactNode }) => <hr className="tw-mb-4">{children}</hr>,
        img: (props: any) =>
          props.src ? (
            <div className="tw-relative tw-h-[600px] tw-aspect-4/3">
              <Image src={props.src} layout="fill" alt={props.alt} objectFit="contain" />
            </div>
          ) : null,

        ...components,
      }}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
};

export default Markdown;
